import { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import { api } from "../../../../api";
import { Button, SmallSpinner, Typography } from "../../../../components";
import { toast } from "../../../../toasts";
import { ConnectWalletButton } from "../../../ConnectWalletButton";
import { LevelSelect } from "./LevelSelect";
import * as S from "./UpgradeBody.styled";
import { UpgradePoolInfo } from "./UpgradePoolInfo";
import { usePoolUpgradeHandler } from "./usePoolUpgradeHandler";

type UpgradeBodyProps = {
  poolLevel: PoolLevel;
  onSuccess: () => void;
};

export const UpgradeBody = (props: UpgradeBodyProps) => {
  const { poolLevel, onSuccess } = props;
  const [otherLevels, setOtherLevels] = useState<PoolLevel[]>();
  const [selectedLevel, setSelectedLevel] = useState<PoolLevel>();
  const { isConnected, chainId } = useAccount();

  const fetchOtherPools = async () => {
    try {
      const {
        data: { data },
      } = await api.pools.getLevels({
        filter: {
          "pool.id": `$eq:${poolLevel.pool.id}`,
          "pool.contract.chain.chainId": `$eq:${chainId}`,
        },
      });

      setOtherLevels(
        data.filter(
          (level) => Number(level.usdcApr) > Number(poolLevel.usdcApr)
        )
      );
    } catch (e: unknown) {
      toast.errorHandler(e, "Failed to fetch other pool's levels");
    }
  };

  useEffect(() => {
    fetchOtherPools();
  }, []);

  useEffect(() => {
    if (otherLevels?.length === 1) {
      setSelectedLevel(otherLevels[0]);
    }
  }, [otherLevels]);

  const handleSelectLevel = (level: PoolLevel) => setSelectedLevel(level);

  const { submitUpgrade, isUpgradeInProgress } = usePoolUpgradeHandler(
    onSuccess,
    poolLevel,
    selectedLevel
  );

  return (
    <S.Wrapper>
      <UpgradePoolInfo poolLevel={poolLevel} />
      <Typography.ModalTitle as="p" style={{ color: "#000" }}>
        Upgrade to:
      </Typography.ModalTitle>
      {otherLevels ? (
        otherLevels.length === 0 ? (
          <Typography.CurrencyValueText style={{ color: "#000" }}>
            There are no available pools with higher APR
          </Typography.CurrencyValueText>
        ) : (
          <>
            <LevelSelect
              levels={otherLevels}
              onLevelSelect={handleSelectLevel}
              selectedLevelId={selectedLevel?.id}
            />
            <Typography.Body style={{ color: "#000" }}>
              All your USDC will be transferred to the new pool and locked on
              the given period
            </Typography.Body>
            {isConnected ? (
              <Button.Primary
                bigSize
                onClick={submitUpgrade}
                disabled={!selectedLevel || isUpgradeInProgress}
              >
                Upgrade
              </Button.Primary>
            ) : (
              <ConnectWalletButton />
            )}
            <Typography.BodySmall style={{ color: "#000" }}>
              * During this action, your earned rewards will be transferred to
              your wallet
            </Typography.BodySmall>
          </>
        )
      ) : (
        <SmallSpinner />
      )}
    </S.Wrapper>
  );
};
